import React, { useState, useEffect, useContext } from 'react';
import { Layout, Button, Drawer, Space, Typography, Badge, Tooltip, Dropdown } from 'antd';
import { 
  MedicineBoxOutlined, 
  CloseOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import DrugInteractionsPanel from './DrugInteractionsPanel';
import RecordingContext from '../session/RecordingContext';

const { Sider } = Layout;
const { Text } = Typography;

const ToolsPanel = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const ctx = useContext(RecordingContext);
  const [activeTool, setActiveTool] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [toolProps, setToolProps] = useState({});

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Listen for custom event to open tools panel
  useEffect(() => {
    const handleOpenToolsPanel = (event) => {
      const { tool, ...props } = event.detail;
      setActiveTool(tool);
      setToolProps(props);
    };

    window.addEventListener('openToolsPanel', handleOpenToolsPanel);
    return () => window.removeEventListener('openToolsPanel', handleOpenToolsPanel);
  }, []);

  // Close panel when navigating to a new page (mobile only)
  useEffect(() => {
    if (isMobile && activeTool) {
      setActiveTool(null);
    }
  }, [location.pathname, isMobile]);

  const tools = [
    {
      key: 'drug-interactions',
      icon: <MedicineBoxOutlined style={{ fontSize: '20px' }} />,
      label: t('tools.drug_interactions'),
      component: DrugInteractionsPanel,
      badge: null // Could show count of active interactions
    },
    // Future tools can be added here:
    // {
    //   key: 'calendar',
    //   icon: <CalendarOutlined style={{ fontSize: '20px' }} />,
    //   label: t('tools.calendar'),
    //   component: CalendarPanel
    // },
    // {
    //   key: 'tasks',
    //   icon: <CheckSquareOutlined style={{ fontSize: '20px' }} />,
    //   label: t('tools.tasks'),
    //   component: TasksPanel
    // }
  ];

  const handleToolClick = (toolKey) => {
    if (activeTool === toolKey) {
      setActiveTool(null);
    } else {
      setActiveTool(toolKey);
    }
  };

  const handleClose = () => {
    setActiveTool(null);
    setToolProps({});
  };

  // Create dropdown menu items for mobile
  const mobileMenuItems = tools.map(tool => ({
    key: tool.key,
    icon: tool.icon,
    label: tool.label,
    onClick: () => setActiveTool(tool.key)
  }));

  // Communicate panel state to layout
  useEffect(() => {
    const panelWidth = activeTool ? 640 : 50;
    const event = new CustomEvent('toolsPanelStateChange', {
      detail: {
        isOpen: !!activeTool,
        width: panelWidth
      }
    });
    window.dispatchEvent(event);
  }, [activeTool]);

  const activeToolData = tools.find(tool => tool.key === activeTool);

  // Check if recording bar is visible
  const isRecordingBarVisible = ctx && ctx.showRecordingBar;

  // On mobile, render as a drawer
  if (isMobile) {
    return (
      <>
        {/* Floating action button for mobile - only show when recording bar is not visible */}
        {!isRecordingBarVisible && (
          <Dropdown
            menu={{ items: mobileMenuItems }}
            placement="topRight"
            trigger={['click']}
          >
            <Button
              type="primary"
              shape="circle"
              icon={<AppstoreOutlined />}
              size="large"
              data-testid="mobile-tools-button"
              style={{
                position: 'fixed',
                bottom: 80,
                right: 16,
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}
            />
          </Dropdown>
        )}
        
        {/* Mobile drawer */}
        <Drawer
          title={
            <div style={{ 
              fontSize: '18px', 
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }}>
              <Space>
                <Tooltip title={t('tools.drug_interactions')}>
                  {activeToolData?.icon}
                </Tooltip>
                {activeToolData?.label || t('tools.title')}
              </Space>
              <Button
                type="text"
                icon={<CloseOutlined />}
                size="small"
                onClick={handleClose}
              />
            </div>
          }
          placement="right"
          width="100%"
          open={!!activeTool}
          onClose={handleClose}
          closable={false}
          styles={{
            header: {
              borderBottom: 'none'
            }
          }}
        >
          {activeToolData && (
            <activeToolData.component 
              onClose={handleClose} 
              {...toolProps}
            />
          )}
        </Drawer>
      </>
    );
  }

  // Desktop sidebar
  return (
    <div
      style={{
        position: 'fixed',
        right: 0,
        top: 0,
        bottom: 0,
        width: activeTool ? 600 : 50,
        backgroundColor: '#fff',
        borderLeft: '1px solid #f0f0f0',
        boxShadow: activeTool ? '-2px 0 8px rgba(0, 0, 0, 0.1)' : 'none',
        zIndex: 800,
        transition: 'width 0.2s ease',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {!activeTool ? (
        // Collapsed state - show tool buttons
        <div style={{ padding: '8px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {tools.map(tool => (
            <Button
              key={tool.key}
              type="text"
              icon={
                <Tooltip title={tool.label}>
                  {tool.icon}
                </Tooltip>
              }
              size="large"
              style={{
                width: '35px',
                height: '35px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '8px'
              }}
              onClick={() => handleToolClick(tool.key)}
              title={tool.label}
            >
              {tool.badge && (
                <Badge count={tool.badge} size="small" />
              )}
            </Button>
          ))}
        </div>
      ) : (
        // Expanded state - show tool content
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <div style={{ 
            padding: '16px', 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Space>
              <Tooltip title={t('tools.drug_interactions')}>
                {activeToolData?.icon}
              </Tooltip>
              <Text strong style={{ fontSize: '18px' }}>{activeToolData?.label}</Text>
            </Space>
            <Button
              type="text"
              icon={<CloseOutlined />}
              size="small"
              onClick={handleClose}
            />
          </div>
          
          {/* Content */}
          <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
            {activeToolData && (
              <activeToolData.component 
                onClose={handleClose} 
                {...toolProps}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ToolsPanel; 